import { useEffect, useState } from 'react'

import { useReactiveVar } from '@apollo/client'
import axios from 'axios'
import getConfig from 'next/config'
import { useCookies } from 'react-cookie'

import PageLoader from '~/components/ui/PageLoader'
import { ForceLogoutError } from '~/components/ui/sharedStyledComponents'
import { ActiveWorkoutCookie } from '~/constants'
import { ErrorEventTypes } from '~/events/eventTypes'
import useTrackEvents from '~/events/trackers/useTrackEvents'
import { currentUserVar, forceLogoutVar } from '~/graphql/reactive-vars'
import logger from '~/utils/logger'
const { publicRuntimeConfig } = getConfig()

interface ForceLogoutWrapperProps {
  children: React.ReactNode
}

const forceLogoutUrl = `${publicRuntimeConfig.authMiddleware.endpoint}/l2/force-logout`

const ForceLogoutWrapper = ({ children }: ForceLogoutWrapperProps) => {
  const [, , removeCookie] = useCookies([ActiveWorkoutCookie])
  const forceLogout = useReactiveVar(forceLogoutVar)
  const [isLoading, setIsLoading] = useState(true)
  const { trackErrorEvent, trackEvent } = useTrackEvents()

  useEffect(() => {
    // TODO: Make sure this is working just like useAuth.tsx -> processOryLogout
    // Move both of them to a single place
    async function forceLogoutHandler() {
      setIsLoading(true)
      try {
        await axios.post(forceLogoutUrl, {}, { withCredentials: true })
        forceLogoutVar(false)
        trackEvent('sign_out', { force_logout: true })
        localStorage.removeItem('ob_action__open_fit_test_page')
        localStorage.removeItem('override_subscription_state')
        removeCookie(ActiveWorkoutCookie, { path: '/' })
        currentUserVar(undefined)
      } catch (err) {
        trackErrorEvent({
          error_type: ErrorEventTypes.ForceLogoutFail,
          error_location: 'Error::ForceLogoutWrapper::ForceLogout',
          error_message: '',
          error_details: err,
        })
        logger.debug('Failed to force logout', err)
      } finally {
        setIsLoading(false)
      }
    }

    if (forceLogout) {
      forceLogoutHandler()
      return
    }
    setIsLoading(false)
  }, [forceLogout, removeCookie, trackErrorEvent, trackEvent])

  if (isLoading) {
    return <PageLoader />
  }

  if (forceLogout) {
    return <ForceLogoutError />
  }

  return <>{children}</>
}

export default ForceLogoutWrapper
